import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, ChevronRight, Calendar, MapPin, FileText, AlertTriangle,
  CheckCircle2, Clock, X, Heart, Activity, User,
} from 'lucide-react';

const ARCHIVIO_PAZIENTI = [
  {
    id: 'arch-001', nome: 'Anna', cognome: 'Bianchi', eta: 82, cf: 'BNCHNN44A41H501K',
    indirizzo: 'Via dei Giubbonari 28, Roma',
    paiCodice: 'PAI-2026-00018', stato: 'attivo',
    ultimaVisita: '15/02/2026', visite: 24, diagnosi: 'Post-intervento protesi anca dx',
    patologie: ['Ipertensione arteriosa', 'Osteoporosi', 'Anemia sideropenica'],
    verbali: [
      { data: '15/02/2026', tipo: 'Prelievo ematico', esito: 'Valori nella norma. Hb 11.2 g/dL', operatore: 'Inf. M. Colombo' },
      { data: '13/02/2026', tipo: 'Parametri vitali', esito: 'PA 135/82, FC 74, SpO2 96%. Nella norma', operatore: 'Inf. M. Colombo' },
      { data: '10/02/2026', tipo: 'Medicazione ferita chirurgica', esito: 'Guarigione in atto. Punti di sutura rimossi', operatore: 'Inf. L. Martini' },
      { data: '07/02/2026', tipo: 'Prelievo ematico + Parametri', esito: 'Creatinina 1.1, Hb 10.8. Lieve anemia', operatore: 'Inf. M. Colombo' },
    ],
  },
  {
    id: 'arch-002', nome: 'Giuseppe', cognome: 'Ferretti', eta: 75, cf: 'FRRGPP51C12H501R',
    indirizzo: 'Viale Aventino 102, Roma',
    paiCodice: 'PAI-2026-00031', stato: 'attivo',
    ultimaVisita: '14/02/2026', visite: 18, diagnosi: 'Lesione da pressione sacrale stadio II',
    patologie: ['Diabete mellito tipo 2', 'Vasculopatia periferica'],
    verbali: [
      { data: '14/02/2026', tipo: 'Medicazione avanzata', esito: 'Lesione 3x2cm, fondo fibrinoso 30%. Applicato collagene', operatore: 'Inf. M. Colombo' },
      { data: '12/02/2026', tipo: 'Medicazione avanzata', esito: 'Miglioramento margini. Detersione con soluzione fisiologica', operatore: 'Inf. M. Colombo' },
      { data: '10/02/2026', tipo: 'Educazione terapeutica', esito: 'Sessione con caregiver su posizionamento e superfici antidecubito', operatore: 'Inf. M. Colombo' },
    ],
  },
  {
    id: 'arch-003', nome: 'Maria Teresa', cognome: 'Conti', eta: 88, cf: 'CNTMRT38E45H501F',
    indirizzo: 'Via Ostiense 234, Roma',
    paiCodice: 'PAI-2026-00045', stato: 'attivo',
    ultimaVisita: '15/02/2026', visite: 10, diagnosi: 'Infezione vie urinarie - Ciclo antibiotico EV',
    patologie: ['Insufficienza renale cronica stadio IIIa', 'Fibrillazione atriale'],
    verbali: [
      { data: '15/02/2026', tipo: 'Terapia EV + Prelievo', esito: 'Ceftriaxone 2g somministrato. Giorno 10/14. Nessuna reazione', operatore: 'Inf. M. Colombo' },
      { data: '14/02/2026', tipo: 'Terapia EV + Parametri', esito: 'PA 128/76, FC 68, SpO2 97%. Terapia regolare', operatore: 'Inf. M. Colombo' },
    ],
  },
  {
    id: 'arch-004', nome: 'Roberto', cognome: 'Mancini', eta: 71, cf: 'MNCBRT55H15H501V',
    indirizzo: 'Via Tiburtina 420, Roma',
    paiCodice: 'PAI-2025-00112', stato: 'chiuso',
    ultimaVisita: '20/12/2025', visite: 42, diagnosi: 'BPCO riacutizzata con ossigenoterapia domiciliare',
    patologie: ['BPCO stadio III', 'Ex fumatore', 'Ansia generalizzata'],
    verbali: [
      { data: '20/12/2025', tipo: 'Visita di chiusura PAI', esito: 'Paziente stabilizzato. SpO2 a riposo 95%. Dimesso dal servizio ADI', operatore: 'Inf. M. Colombo' },
      { data: '18/12/2025', tipo: 'Parametri vitali + Educazione', esito: 'Corretto utilizzo ossigenoterapia. PA 140/85', operatore: 'Inf. M. Colombo' },
    ],
  },
  {
    id: 'arch-005', nome: 'Lucia', cognome: 'Santini', eta: 79, cf: 'SNTLCU47D65H501P',
    indirizzo: 'Via Appia Nuova 678, Roma',
    paiCodice: 'PAI-2025-00098', stato: 'chiuso',
    ultimaVisita: '05/11/2025', visite: 36, diagnosi: 'Frattura femore sx - Riabilitazione post-chirurgica',
    patologie: ['Osteoporosi severa', 'Ipotiroidismo'],
    verbali: [
      { data: '05/11/2025', tipo: 'Visita di chiusura PAI', esito: 'Deambulazione autonoma con bastone. Obiettivi PAI raggiunti', operatore: 'Inf. M. Colombo' },
    ],
  },
];

const STATO_BADGE = {
  attivo: { label: 'PAI Attivo', className: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0' },
  chiuso: { label: 'PAI Chiuso', className: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-0' },
};

export default function OperatoreArchivio() {
  const [search, setSearch] = useState('');
  const [selectedPaz, setSelectedPaz] = useState(null);
  const [filterStato, setFilterStato] = useState('tutti');

  const filtered = ARCHIVIO_PAZIENTI.filter(p => {
    const matchSearch = `${p.nome} ${p.cognome} ${p.cf} ${p.paiCodice}`.toLowerCase().includes(search.toLowerCase());
    const matchStato = filterStato === 'tutti' || p.stato === filterStato;
    return matchSearch && matchStato;
  });

  return (
    <AppLayout role="operatore">
      <div className="space-y-5" data-testid="operatore-archivio">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">Archivio Pazienti</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Storico pazienti visitati e verbali prestazioni</p>
          </div>
          <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-0 text-xs font-bold">
            {ARCHIVIO_PAZIENTI.length} pazienti
          </Badge>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per nome, cognome, CF o codice PAI..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl text-sm"
              data-testid="archivio-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {['tutti', 'attivo', 'chiuso'].map(f => (
              <button
                key={f}
                onClick={() => setFilterStato(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStato === f ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                data-testid={`filter-${f}`}
              >
                {f === 'tutti' ? 'Tutti' : f === 'attivo' ? 'Attivi' : 'Chiusi'}
              </button>
            ))}
          </div>
        </div>

        {/* Patient List */}
        <div className="flex flex-col gap-3">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400">Nessun paziente trovato</div>
          ) : (
            filtered.map(p => {
              const badge = STATO_BADGE[p.stato];
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPaz(p)}
                  className="w-full text-left bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all group"
                  data-testid={`paziente-card-${p.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 dark:bg-teal-950/30 border border-teal-200/40 dark:border-teal-800/40 flex items-center justify-center text-sm font-bold text-teal-700 dark:text-teal-300 shrink-0">
                      {p.nome[0]}{p.cognome[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{p.cognome} {p.nome}</p>
                        <Badge className={`${badge.className} text-[9px] font-bold`}>{badge.label}</Badge>
                      </div>
                      <p className="text-[11px] text-slate-400 truncate">{p.diagnosi}</p>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Ultima: {p.ultimaVisita}</span>
                        <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{p.visite} visite</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.indirizzo.split(',')[0]}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 dark:group-hover:text-slate-300 transition-colors shrink-0" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Drawer Verbali Paziente */}
      {selectedPaz && (
        <div className="fixed inset-0 z-50" data-testid="drawer-verbali">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedPaz(null)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[520px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-950/30 flex items-center justify-center text-sm font-bold text-teal-700 dark:text-teal-300">
                  {selectedPaz.nome[0]}{selectedPaz.cognome[0]}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-white">{selectedPaz.cognome} {selectedPaz.nome}</p>
                  <p className="text-[10px] text-slate-400">{selectedPaz.paiCodice} — {selectedPaz.eta} anni</p>
                </div>
              </div>
              <button onClick={() => setSelectedPaz(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Info Cliniche */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Informazioni Cliniche</p>
                <p className="text-xs text-slate-700 dark:text-slate-200 font-medium mb-2">{selectedPaz.diagnosi}</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedPaz.patologie.map(pat => (
                    <Badge key={pat} className="bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 text-[9px] font-medium">
                      {pat}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Visite</p>
                  <p className="text-lg font-bold text-slate-800 dark:text-white">{selectedPaz.visite}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Ultima</p>
                  <p className="text-xs font-bold text-slate-800 dark:text-white mt-1">{selectedPaz.ultimaVisita}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Stato</p>
                  <Badge className={`${STATO_BADGE[selectedPaz.stato].className} text-[9px] font-bold mt-1`}>{STATO_BADGE[selectedPaz.stato].label}</Badge>
                </div>
              </div>

              {/* Verbali Timeline */}
              <div>
                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Verbali Prestazioni</p>
                <div className="flex flex-col gap-0 relative">
                  <div className="absolute left-[17px] top-4 bottom-4 w-px bg-slate-200 dark:bg-slate-700" />
                  {selectedPaz.verbali.map((v, i) => (
                    <div key={i} className="relative flex gap-4 py-3">
                      <div className={`w-[34px] h-[34px] rounded-full flex items-center justify-center shrink-0 z-10 ${i === 0 ? 'bg-teal-100 dark:bg-teal-900/30 border-2 border-teal-400' : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'}`}>
                        <FileText className={`w-3.5 h-3.5 ${i === 0 ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-semibold text-slate-800 dark:text-white">{v.tipo}</p>
                          <span className="text-[10px] text-slate-400">{v.data}</span>
                        </div>
                        <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">{v.esito}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{v.operatore}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
